/**
 * Add or edit a supplier — the prototype's detail sheet, made editable.
 *
 * The form holds text in the units a manager types (dollars, a time), and
 * converts at the edge to what the API stores (cents, `HH:MM`). The display
 * string for delivery days (`Tue, Fri · before 11:00`) is composed here from
 * the structured fields so the two never disagree.
 */

import { useState, type FormEvent } from "react";
import type { Supplier } from "./api.js";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
const CHANNELS = [
  ["email", "Email"],
  ["ordermentum", "API · Ordermentum"],
  ["phone", "Phone"],
] as const;

export interface SupplierFormValues {
  name: string;
  category: string;
  rep: string;
  phone: string;
  email: string;
  channel: "email" | "ordermentum" | "phone";
  terms: string;
  cutoffWeekdays: string[];
  cutoffTime: string;
  deliveryDays: string[];
  minimumExGst: string;
  leadDays: string;
  abn: string;
  account: string;
}

function valuesFrom(s: Supplier | undefined): SupplierFormValues {
  return {
    name: s?.name ?? "",
    category: s?.category ?? "",
    rep: s?.rep ?? "",
    phone: s?.phone ?? "",
    email: s?.email ?? "",
    channel: s?.channel ?? "email",
    terms: s?.terms ?? "",
    cutoffWeekdays: [...(s?.cutoffWeekdays ?? [])],
    cutoffTime: s?.cutoffTime ?? "",
    deliveryDays: [...(s?.deliveryDays ?? [])],
    minimumExGst: s?.minimumExGstCents ? (s.minimumExGstCents / 100).toFixed(2) : "",
    leadDays: String(s?.leadDays ?? 1),
    abn: s?.abn ?? "",
    account: s?.account ?? "",
  };
}

/** The request body: cents, a composed display string, no empty strings. */
export function supplierBody(v: SupplierFormValues): Record<string, unknown> {
  const trimmed = (t: string) => t.trim();
  const cutoff =
    v.cutoffWeekdays.length && v.cutoffTime
      ? `${v.cutoffWeekdays.join(", ")} · before ${v.cutoffTime}`
      : v.cutoffWeekdays.length
        ? v.cutoffWeekdays.join(", ")
        : "";
  const minimum = v.minimumExGst.trim() === "" ? 0 : Math.round(Number(v.minimumExGst) * 100);
  const body: Record<string, unknown> = {
    name: trimmed(v.name),
    category: trimmed(v.category),
    rep: trimmed(v.rep),
    phone: trimmed(v.phone),
    email: trimmed(v.email),
    channel: v.channel,
    terms: trimmed(v.terms),
    cutoff,
    cutoffWeekdays: v.cutoffWeekdays,
    deliveryDays: v.deliveryDays,
    minimumExGstCents: minimum,
    leadDays: Number(v.leadDays || 1),
    abn: trimmed(v.abn),
    account: trimmed(v.account),
  };
  if (v.cutoffTime) body["cutoffTime"] = v.cutoffTime;
  return body;
}

interface Props {
  readonly initial?: Supplier | undefined;
  readonly onCancel: () => void;
  readonly onSave: (body: Record<string, unknown>) => Promise<void>;
}

export function SupplierForm({ initial, onCancel, onSave }: Props) {
  const [v, setV] = useState<SupplierFormValues>(() => valuesFrom(initial));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof SupplierFormValues>(k: K, value: SupplierFormValues[K]) =>
    setV((s) => ({ ...s, [k]: value }));
  const toggle = (k: "cutoffWeekdays" | "deliveryDays", day: string) =>
    setV((s) => ({
      ...s,
      [k]: s[k].includes(day)
        ? s[k].filter((d) => d !== day)
        : [...WEEKDAYS].filter((d) => d === day || s[k].includes(d)),
    }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!v.name.trim()) return setError("A supplier needs a name.");
    if (v.minimumExGst && !Number.isFinite(Number(v.minimumExGst))) return setError("Minimum must be a number of dollars.");
    if (!/^\d+$/.test(v.leadDays)) return setError("Lead time must be whole days.");
    if (v.cutoffTime && !/^([01]\d|2[0-3]):[0-5]\d$/.test(v.cutoffTime)) return setError("Cut-off must be a 24-hour time such as 11:00.");
    setBusy(true);
    try {
      await onSave(supplierBody(v));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the supplier");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="sup-form" onSubmit={submit}>
      <div className="label" style={{ gridColumn: "1 / -1" }}>
        {initial ? `Editing ${initial.name}` : "New supplier"}
      </div>
      <label className="field" style={{ gridColumn: "1 / -1" }}>
        <span>Name</span>
        <input type="text" value={v.name} onChange={(e) => set("name", e.target.value)} disabled={Boolean(initial)} required />
        {initial && <span style={{ textTransform: "none", letterSpacing: 0 }}>The id comes from the name, so it cannot change once created.</span>}
      </label>
      <label className="field"><span>Category</span><input type="text" placeholder="Spirits and amari" value={v.category} onChange={(e) => set("category", e.target.value)} /></label>
      <label className="field"><span>Account manager</span><input type="text" value={v.rep} onChange={(e) => set("rep", e.target.value)} /></label>
      <label className="field"><span>Direct line</span><input type="text" value={v.phone} onChange={(e) => set("phone", e.target.value)} /></label>
      <label className="field"><span>Orders inbox</span><input type="email" value={v.email} onChange={(e) => set("email", e.target.value)} /></label>
      <label className="field">
        <span>Order method</span>
        <select value={v.channel} onChange={(e) => set("channel", e.target.value as SupplierFormValues["channel"])}>
          {CHANNELS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </label>
      <label className="field"><span>Payment terms</span><input type="text" placeholder="30 days from invoice" value={v.terms} onChange={(e) => set("terms", e.target.value)} /></label>

      <div className="field">
        <span>Order cut-off days</span>
        <div className="chips">
          {WEEKDAYS.map((d) => (
            <button type="button" key={d} className="btn sm" aria-pressed={v.cutoffWeekdays.includes(d)} onClick={() => toggle("cutoffWeekdays", d)}>{d}</button>
          ))}
        </div>
      </div>
      <label className="field"><span>Cut-off time (24 h)</span><input type="text" placeholder="11:00" value={v.cutoffTime} onChange={(e) => set("cutoffTime", e.target.value)} /></label>
      <div className="field" style={{ gridColumn: "1 / -1" }}>
        <span>Delivery days</span>
        <div className="chips">
          {WEEKDAYS.map((d) => (
            <button type="button" key={d} className="btn sm" aria-pressed={v.deliveryDays.includes(d)} onClick={() => toggle("deliveryDays", d)}>{d}</button>
          ))}
        </div>
      </div>
      <label className="field"><span>Minimum order, $ ex GST</span><input className="num-input" style={{ width: "100%" }} type="number" min={0} step={1} value={v.minimumExGst} onChange={(e) => set("minimumExGst", e.target.value)} /></label>
      <label className="field"><span>Lead time, days</span><input className="num-input" style={{ width: "100%" }} type="number" min={0} max={90} step={1} value={v.leadDays} onChange={(e) => set("leadDays", e.target.value)} /></label>
      <label className="field"><span>ABN</span><input type="text" value={v.abn} onChange={(e) => set("abn", e.target.value)} /></label>
      <label className="field"><span>Our account</span><input type="text" value={v.account} onChange={(e) => set("account", e.target.value)} /></label>

      {error && <div className="msg err" style={{ gridColumn: "1 / -1" }}>{error}</div>}
      <div className="row" style={{ gridColumn: "1 / -1", justifyContent: "flex-end" }}>
        <button type="button" className="btn" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="submit" className="btn primary" style={{ height: 48 }} disabled={busy}>
          {busy ? "Saving…" : initial ? "Save changes" : "Create supplier"}
        </button>
      </div>
    </form>
  );
}
